import { ConfigValue, dynamicLinksService, remoteConfig, utmAppname } from '@/services/RemoteConfig'
import axios from 'axios'
import { ShortenLinkRequest, Suffix } from '@/dto/ShortenLinkRequest'
import { language } from './Api'
import { encryptToBytes } from './EncryptionUtils'
import { firebaseApp } from './Firebase'

// Google Campaign source
const utmSource = utmAppname
let utmMedium = 'web_app'

// sets the utmMedium src to be used when creating links with campaign parameters
export function setUtmMedium(toMedium: string) {
  utmMedium = toMedium
}

// creates a shortened dynamic link from return value of function createDynamicLink()
export async function createDynamicShareLink(forItemId: string): Promise<string> {
  if (!forItemId) {
    return remoteConfig.getValue(ConfigValue.deepLinkDomain).asString()
  }

  let token: string

  const currentUser = firebaseApp.auth().currentUser

  if (currentUser) {
    // User already signed in
    token = await currentUser.getIdToken()
  } else {
    // Sign in anonymously first
    const userCredential = await firebaseApp.auth().signInAnonymously()
    token = await userCredential.user?.getIdToken() ?? ''
  }

  const reqBody = getDynamicLinkBody(forItemId)
  const reqHeader = {
    firebaseAuthToken: token,
    hl: language,
    cc: language.toUpperCase(),
    versionName: '0.1',
    versionCode: '0',
    os: 'ios'
  }

  try {
    const response = await axios.post(
      dynamicLinksService + '/shortenLink',
      encryptToBytes(JSON.stringify(reqBody)),
      { headers: reqHeader }
    )

    if (response.data.link) {
      return response.data.link
    }
    return reqBody.dynamicLinkInfo.domainUriPrefix
  } catch (err) {
    console.error(err)
    return reqBody.dynamicLinkInfo.domainUriPrefix
  }
}

export function createAppstoreLink(): string {
  const iosID = remoteConfig.getValue(ConfigValue.shareIOSId).asString()

  return (
    'https://apps.apple.com/app/apple-store/' +
    iosID +
    '?ct=' + utmSource + // apple campaign name
    '&at=' + utmMedium + // apple affiliate token
    '&platform=iphone' // show iPhone screenshots
  )
}

export function createGoogleStoreLink(): string {
  const androidPackage = remoteConfig.getValue(ConfigValue.shareAndroidPackage).asString()

  return (
    'https://play.google.com/store/apps/details' +
    '?id=' + androidPackage +
    '&referrer=' +
    'utm_source%3D' + utmSource + // google campaign name
    '%26utm_medium%3D' + utmMedium // google medium
  )
}

function getDynamicLinkBody(forItemId: string): ShortenLinkRequest {
  const domain = remoteConfig.getValue(ConfigValue.deepLinkDomain).asString()

  let link = remoteConfig.getValue(ConfigValue.deepLinkItem).asString().replace('{itemId}', forItemId)
  if (!link.startsWith('http')) {
    // Links without ssl will result in invalid firebase error
    link = 'https://' + link
  }

  const iosPackage = remoteConfig.getValue(ConfigValue.shareIOSPackage).asString()
  const iosID = remoteConfig.getValue(ConfigValue.shareIOSId).asString()
  const androidPackage = remoteConfig.getValue(ConfigValue.shareAndroidPackage).asString()

  // skips app-preview-page
  const efr = true

  return {
    dynamicLinkInfo: {
      domainUriPrefix: domain,
      link: link,
      navigationInfo: {
        enableForcedRedirect: efr
      },
      androidInfo: {
        androidPackageName: androidPackage
      },
      iosInfo: {
        iosBundleId: iosPackage,
        iosAppStoreId: iosID
      },
      analyticsInfo: {
        googlePlayAnalytics: {
          utmSource: utmSource,
          utmMedium: utmMedium
        },
        itunesConnectAnalytics: {
          ct: utmSource,
          at: utmMedium
        }
      }
    },
    suffix: Suffix.SHORT
  }
}
